/**
 * Where the pieces are wired together — the one place that knows about all of
 * them, so nothing else has to.
 *
 * Deliberately a function, not a module-level singleton: the tests build a
 * whole app against fakes, and a singleton would leak one test's state into the
 * next. It also makes the dependency direction obvious — auth feeds the HTTP
 * layer, the HTTP layer feeds the client, the client and the local store feed
 * the queue, and nothing reaches backwards.
 */

import { AUTH, SITE } from '../config/lists.js';
import { SpHttp, type TokenSource } from '../api/http.js';
import { LIST, SpClient, type ProjectRef, type ProjectScope } from '../api/sp.js';
import { openLocalStore, type LocalStore } from '../db/local.js';
import { EvidenceLibrary } from '../data/evidence.js';
import { SyncQueue, type SyncTransport } from '../sync/queue.js';

/** Bridges the scoped client to the queue's narrower view of a server. */
export function transportFor(scope: ProjectScope): SyncTransport {
  return {
    async findBySyncId(list, offlineSyncId) {
      // The idempotency lookup goes through the scope like everything else, so
      // it can never adopt a row belonging to another project.
      const rows = await scope.list<{ Id: number }>(list as never, {
        filter: `OfflineSyncId eq '${offlineSyncId.replace(/'/g, "''")}'`,
        select: ['Id'],
        top: 1,
      });
      return rows[0]?.Id ?? null;
    },
    async create(list, fields) {
      const created = await scope.create<{ Id: number }>(list as never, fields);
      return created.Id;
    },
    async update(list, itemId, fields) {
      await scope.update(list as never, itemId, fields);
    },
  };
}

export interface App {
  client: SpClient;
  local: LocalStore;
  /** Everything project-specific hangs off here; null until a project is chosen. */
  session: {
    scope: ProjectScope;
    queue: SyncQueue;
    project: ProjectRef;
    /** Photos go here. Built per session so the library path is settled in one place. */
    evidence: EvidenceLibrary;
  } | null;
  open(project: ProjectRef): void;
  close(): void;
}

export interface AppOptions {
  getToken: TokenSource;
  siteUrl?: string;
  /** Server-relative path of the evidence library. */
  evidencePath?: string;
  dbName?: string;
  fetch?: typeof globalThis.fetch;
}

export async function createApp(opts: AppOptions): Promise<App> {
  const http = new SpHttp({
    siteUrl: opts.siteUrl ?? SITE.absoluteUrl,
    getToken: opts.getToken,
    ...(opts.fetch ? { fetch: opts.fetch } : {}),
  });
  const client = new SpClient(http);
  const local = await openLocalStore(opts.dbName);

  const app: App = {
    client,
    local,
    session: null,
    open(project) {
      const scope = client.scope(project);
      app.session = {
        scope,
        project,
        queue: new SyncQueue(local.queue, transportFor(scope)),
        evidence: new EvidenceLibrary(http, opts.evidencePath ?? `${SITE.serverRelativeUrl}/Cx Evidence`),
      };
    },
    close() {
      app.session = null;
      local.close();
    },
  };
  return app;
}

/** Re-exported so callers do not reach into two modules for one screen. */
export { LIST, AUTH };
